import React from 'react'
import { css } from '@emotion/core'
import { Container, Box, Grid } from '@material-ui/core'
import * as Yup from 'yup'
import { Formik } from 'formik'

import * as utils from '~/utils'
import { Theme } from '~/styles'
import { Heading, Typography, Button, Select, Header, Input, Main } from '~/components'
import { handleChange, handleSelect, handleBlur } from '~/utils/formik'
import Loader from '~/components/Loader/Loader'
import ErrorIcon from '~/assets/icons/material/error.svg?react'
import {
  GENDERS_DICT,
  KeyValueEntry,
  PreferredLanguageEntry,
  StateItemsProps,
} from '~/screens/signup/steps/account/utils'
import { PreferredLanguage } from '~/types'

export interface DemographicsCollectionFormValues {
  state: StateItemsProps | null
  sex: KeyValueEntry | null
  gender: KeyValueEntry | null
  pronouns: KeyValueEntry | null
  preferredLanguage?: PreferredLanguageEntry | null
  preferredLanguageOther?: string
}

interface DemographicsCollectionFormUIProps {
  initialValues: DemographicsCollectionFormValues
  onSubmit: (values: DemographicsCollectionFormValues) => void | Promise<void>
  isLoading?: boolean
  submitting?: boolean
  states: any[]
  pronouns: any[]
  genderIdentities: any[]
  preferredLanguages?: PreferredLanguage[]
}

const DEFAULT_LANGUAGE_NAME = 'English'
export const OTHER_LANGUAGE_NAME = 'Other'
const OTHER_LANGUAGE_LABEL = 'I prefer another language'

const mapToPreferredLanguageData = (
  preferredLanguage: PreferredLanguage,
): PreferredLanguageEntry => ({
  name: preferredLanguage.name,
  label: preferredLanguage.label,
  value: String(preferredLanguage.value),
})

export const getDefaultPreferredLanguage = (
  preferredLanguages: PreferredLanguage[] | undefined,
): PreferredLanguageEntry | null => {
  const defaultLanguage = preferredLanguages?.find(
    prefLang => prefLang.name === DEFAULT_LANGUAGE_NAME,
  )
  return defaultLanguage ? mapToPreferredLanguageData(defaultLanguage) : null
}

export const DemographicsCollectionFormUI: React.FC<DemographicsCollectionFormUIProps> = ({
  initialValues,
  onSubmit,
  isLoading,
  submitting,
  states,
  pronouns,
  genderIdentities,
  preferredLanguages,
}) => {
  const validationSchema = Yup.object().shape({
    sex: Yup.object().required('Sex assigned at birth is required').nullable(),
    state: Yup.object()
      .shape({ label: Yup.string(), value: Yup.string() })
      .nullable()
      .required('State of residence is required'),
    preferredLanguage: Yup.object()
      .shape({ label: Yup.string(), value: Yup.string() })
      .nullable()
      .required('Preferred language is required'),
    preferredLanguageOther: Yup.string().when('preferredLanguage', {
      is: preferredLanguage => preferredLanguage.name === OTHER_LANGUAGE_NAME,
      then: utils.PreferredLanguageOtherSchema,
    }),
  })

  const getPreferredLanguageData = (
    preferredLanguages: PreferredLanguage[] | undefined,
  ): PreferredLanguageEntry[] | undefined => {
    if (preferredLanguages) {
      return preferredLanguages
        .map(prefLang =>
          prefLang.name === OTHER_LANGUAGE_NAME
            ? { ...prefLang, label: OTHER_LANGUAGE_LABEL }
            : prefLang,
        )
        .map(mapToPreferredLanguageData)
    }
    return undefined
  }

  return isLoading ? (
    <>
      <Header />
      <Loader />
    </>
  ) : (
    <>
      <Header />
      <Container>
        <Box mt={10}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} md={6} lg={5}>
              <Main px={{ xs: 0 }}>
                <Heading>Tell us a bit more about yourself</Heading>
                <Typography variant="subText">
                  Your answers help us provide the right care for your specific needs.
                </Typography>
                <Box mt={3} />
                <Formik<DemographicsCollectionFormValues>
                  validateOnChange={false}
                  initialValues={initialValues}
                  enableReinitialize={true}
                  onSubmit={onSubmit}
                  validationSchema={validationSchema}
                >
                  {formik => (
                    <form onSubmit={formik.handleSubmit} css={styles.wrapper}>
                      <Box mb={2}>
                        <Select
                          data-cy="state"
                          onChange={handleSelect(formik, 'state')}
                          error={formik.touched.state && !!formik.errors.state}
                          placeholder="State of residence"
                          options={states}
                          value={formik.values.state}
                        />
                        {formik.touched?.state && formik.errors?.state && (
                          <Typography el="span" css={styles.errorText}>
                            {formik.errors?.state}
                          </Typography>
                        )}
                      </Box>
                      <Box mb={2}>
                        <Select
                          data-cy="sex"
                          onChange={handleSelect(formik, 'sex', false)}
                          error={formik.touched.sex && !!formik.errors.sex}
                          placeholder="Sex assigned at birth"
                          options={GENDERS_DICT}
                          value={formik.values.sex}
                        />
                        {formik.touched?.sex && formik.errors?.sex && (
                          <div css={styles.errorDiv}>
                            <ErrorIcon css={styles.errorIcon} />
                            <Typography el="span" css={styles.errorText}>
                              {formik.errors?.sex}
                            </Typography>
                          </div>
                        )}
                      </Box>
                      <Box mb={2}>
                        <Select
                          data-cy="pronouns"
                          onChange={handleSelect(formik, 'pronouns', false)}
                          error={formik.touched.pronouns && !!formik.errors.pronouns}
                          placeholder="Pronouns (optional)"
                          options={pronouns}
                          value={formik.values.pronouns}
                        />
                      </Box>
                      <Box mb={2}>
                        <Select
                          data-cy="gender"
                          onChange={handleSelect(formik, 'gender', false)}
                          error={formik.touched.gender && !!formik.errors.gender}
                          placeholder="Gender identity (optional)"
                          options={genderIdentities}
                          value={formik.values.gender}
                        />
                      </Box>
                      <Box mb={2}>
                        <Select
                          data-cy="preferredLanguage"
                          onChange={handleSelect(formik, 'preferredLanguage')}
                          error={
                            formik.touched.preferredLanguage && !!formik.errors.preferredLanguage
                          }
                          placeholder="Preferred language"
                          options={getPreferredLanguageData(preferredLanguages)}
                          value={formik.values.preferredLanguage}
                        />
                        {formik.touched?.preferredLanguage && formik.errors?.preferredLanguage && (
                          <Typography el="span" css={styles.errorText}>
                            {formik.errors?.preferredLanguage}
                          </Typography>
                        )}
                      </Box>
                      {formik.values.preferredLanguage?.name === OTHER_LANGUAGE_NAME && (
                        <Box mb={2}>
                          <Input
                            data-cy="preferredLanguageOther"
                            label="Preferred language"
                            name="preferredLanguageOther"
                            error={
                              formik.touched.preferredLanguageOther
                                ? formik.errors.preferredLanguageOther
                                : void 0
                            }
                            value={formik.values.preferredLanguageOther || ''}
                            autoComplete="language"
                            onChange={handleChange(formik, 'preferredLanguageOther')}
                            onBlur={handleBlur(formik, 'preferredLanguageOther')}
                          />
                        </Box>
                      )}
                      <Box my={5}>
                        <Button
                          data-cy="submit"
                          fullWidth
                          type="submit"
                          disabled={isLoading}
                          css={styles.nextButton}
                          loading={submitting}
                        >
                          Next
                        </Button>
                      </Box>
                    </form>
                  )}
                </Formik>
              </Main>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  )
}

const styles = {
  button: css({
    padding: 0,
    fontWeight: 600,
  }),
  wrapper: () => css({ width: '100%' }),
  image: () =>
    css({
      marginTop: '8.0625em',
    }),
  genderLabel: (theme: Theme) =>
    css({
      color: theme.colors.gray['600'],
    }),
  errorText: (theme: Theme) =>
    css({
      fontSize: '1.2rem',
      color: theme.colors.red[700],
    }),
  stepText: (theme: Theme) =>
    css({
      fontSize: '1.5rem',
      fontWeight: 'normal',
      display: 'block',
      marginTop: '2.875em',
      paddingBottom: '0.5em',
      color: theme.colors.navy[500],
    }),
  subText: (theme: Theme) =>
    css({
      fontSize: '18px',
      lineHeight: '24px',
      fontFamily: 'Source Sans Pro',
      fontWeigth: '400',
      color: theme.colors.navy[500],
    }),
  errorDiv: (theme: Theme) =>
    css({
      flex: 1,
      marginTop: -6,
      marginBottom: 8,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    }),
  errorIcon: (theme: Theme) =>
    css({
      color: theme.colors.red[700],
      fontSize: '1.5rem',
      marginRight: 6,
    }),
  nextButton: (theme: Theme) =>
    css({
      fontFamily: 'Source Sans Pro',
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: '24px',
      minHeight: 48,
      paddingTop: '12px !important',
      paddingBottom: '12px !important',
      background: theme.colors.navy[500],
      '&:hover': {
        background: theme.colors.navy[700],
      },
    }),
}
